import { CatToast } from 'components/blocks-components';
import { useState, useId, ComponentProps } from 'react';

type ToastPositionProps = {
  horizontalPosition?: 'center' | 'left' | 'right';
  verticalPosition?: 'top' | 'bottom';
};
type ToastProps = ComponentProps<typeof CatToast> & {
  message: string;
};
type StatusToastProps = ToastProps & { delay?: number } & {
  position?: ToastPositionProps;
};

/**
 * useToast can be used to create a custom toast when paired with the Toast component
 * For easier implementation, useQuickToast may be a better choice
 *
 * @returns {object} Methods related to handling Toast messages
 */
export const useToast = () => {
  const [showToast, setShowToast] = useState(false);
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [delay, setDelay] = useState(3);
  const [className, setClassName] = useState('');
  const [position, setPosition] = useState<{
    horizontalPosition?: 'center' | 'left' | 'right';
    verticalPosition?: 'top' | 'bottom';
  }>({
    horizontalPosition: 'center',
    verticalPosition: 'top'
  });
  const internalID = useId();

  const closeToast = () => setShowToast(false);

  const openToast = (delay: number) => {
    setShowToast(true);
    setDelay(delay);
    setTimeout(closeToast, delay * 1000);
  };

  const addToast = (
    { message, variant, content }: ToastProps,
    position: ToastPositionProps,
    className: string
  ) => {
    const newToast = {
      key: internalID,
      variant,
      content,
      message
    };
    setPosition(position);
    setToasts([newToast]);
    setClassName(className);
  };

  const success = ({
    message,
    variant = 'success',
    content,
    delay = 3,
    autoClose = false,
    className,
    position
  }: StatusToastProps) => {
    addToast({ message, variant, content }, position, className);
    openToast(delay);
  };

  const error = ({
    message,
    variant = 'error',
    content,
    delay = 3,
    position,
    className
  }: StatusToastProps) => {
    addToast({ message, variant, content }, position, className);
    openToast(delay);
  };

  const warning = ({
    message,
    variant = 'warning',
    content,
    delay = 3,
    position,
    className
  }: StatusToastProps) => {
    addToast({ message, variant, content }, position, className);
    openToast(delay);
  };

  return {
    addToast,
    closeToast,
    openToast,
    showToast,
    toasts,
    success,
    error,
    warning,
    delay,
    className,
    position
  };
};
